import {useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ManualPopover} from "@pg-design/floating-module";
import {Hidden} from "@pg-design/grid-module";
import {BrandWeightIcon, CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useKMLink} from "../../hooks/use_km_link";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useFavourites} from "../hooks/use_favourites";
import {useFavouritesAmplitudeEntities} from "../hooks/use_favourites_amplitude_entities";
import {IAmplitudeFavouritesEventParams} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeAddToComparison} from "../tracking/track_amplitude_add_to_comparison";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";

interface IProps {
    offerId: number;
    propertyId: number;
}

export const PropertyHeaderCompareButton = (props: IProps) => {
    const {offerId, propertyId} = props;
    const {addPropertyToComparison, removePropertyFromComparison, isPropertyInComparison, isPropertyInFavourites} = useFavourites();
    const {offer, property} = useFavouritesAmplitudeEntities({offerId, propertyId});
    const isAuthenticated = useIsAuthenticated();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const comparatorLink = useKMLink(kmAppLink.site.offers.favouritesComparatorWithParams);
    const isInComparison = isPropertyInComparison(propertyId);
    const comparisonEventParams: IAmplitudeFavouritesEventParams = {
        location: "property_page",
        favouritePlacement: "rp_favourite_property_compare",
        offer,
        property
    };

    const handleComparisonClick = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            if (isInComparison) {
                setIsTooltipOpen(false);
                await removePropertyFromComparison(propertyId);
                return;
            }

            const wasAlreadyInFavourites = isPropertyInFavourites(propertyId);
            const wasAdded = await addPropertyToComparison(propertyId);

            if (wasAdded && isAuthenticated) {
                trackAmplitudeAddToComparison(comparisonEventParams);

                if (!wasAlreadyInFavourites) {
                    trackAmplitudeAddToFavourites(comparisonEventParams);
                }

                setIsTooltipOpen(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Hidden className={compareButtonWrapperStyle} on="mobile">
            <ManualPopover
                className={compareTooltipStyle}
                handleSetIsOpen={setIsTooltipOpen}
                hideArrow
                isOpen={isTooltipOpen}
                placement="bottom-end"
                popover={
                    <div
                        aria-live="polite"
                        className={compareTooltipBodyStyle}
                        data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_TOOLTIP}
                        onClick={(event) => event.stopPropagation()}
                        role="status"
                    >
                        <Text as="p" color="var(--colors-white)" variant="info_txt_1">
                            Oferta została dodana do porównywarki i zapisana w ulubionych.{" "}
                            <a
                                className={compareTooltipLinkStyle}
                                data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_TOOLTIP_LINK}
                                href={comparatorLink}
                                onClick={() => {
                                    trackAmplitudeButtonClick({
                                        buttonName: "rp_go_to_comparison",
                                        value: "km_offer_comparison",
                                        buttonCategory: "property_page",
                                        itemId: offerId,
                                        propertyId,
                                        buttonLink: comparatorLink
                                    });
                                }}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Przejdź do porównywarki
                            </a>
                        </Text>
                        <Button
                            aria-label="Zamknij"
                            className={compareTooltipCloseStyle}
                            iconLeft={CloseIcon}
                            iconLeftProps={{
                                fill: "var(--colors-white)",
                                size: "1.6",
                                wrapperColor: "transparent",
                                wrapperSize: "1.6"
                            }}
                            onClick={() => setIsTooltipOpen(false)}
                            type="button"
                            variant="transparent"
                        />
                    </div>
                }
            >
                {({refs, getReferenceProps}) => (
                    <div ref={refs.setReference} {...getReferenceProps()} onClick={(event) => event.stopPropagation()}>
                        <Button
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_BUTTON}
                            iconLeft={BrandWeightIcon}
                            iconLeftProps={{className: compareIconStyle}}
                            isLoading={isLoading}
                            onClick={handleComparisonClick}
                            size="small"
                            type="button"
                            variant={isInComparison ? "filled_secondary" : "outlined_secondary"}
                        >
                            {isInComparison ? "Usuń z porównywarki" : "Dodaj do porównania"}
                        </Button>
                    </div>
                )}
            </ManualPopover>
        </Hidden>
    );
};

const compareButtonWrapperStyle = css({
    ml: "auto"
});

const compareIconStyle = css({
    mr: 1.5
});

const compareTooltipStyle = css({
    zIndex: "2",
    mt: "1rem"
});

const compareTooltipBodyStyle = css({
    display: "flex",
    position: "relative",
    alignItems: "flex-start",
    gap: 1,
    p: 1,
    w: "22.4rem",
    bg: "var(--colors-gray-800)",
    borderRadius: 1,
    boxShadow: "var(--shadow-deep)",
    _before: {
        content: '""',
        position: "absolute",
        top: "-1.2rem",
        right: 3,
        w: "3.1rem",
        h: "1.2rem",
        bg: "var(--colors-gray-800)",
        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)"
    }
});

const compareTooltipLinkStyle = css({
    color: "inherit",
    textDecoration: "underline"
});

const compareTooltipCloseStyle = css({
    flex: "0 0 auto",
    minWidth: "unset",
    width: "auto",
    h: "unset",
    p: 0
});
import {useState} from "react";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {ManualPopover} from "@pg-design/floating-module";
import {Hidden} from "@pg-design/grid-module";
import {BrandWeightIcon, CloseIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {kmAppLink} from "@pg-mono/km-routes";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {useKMLink} from "../../hooks/use_km_link";
import {trackAmplitudeButtonClick} from "../../tracking/amplitude/track_amplitude_button_click";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {useFavourites} from "../hooks/use_favourites";
import {useFavouritesAmplitudeEntities} from "../hooks/use_favourites_amplitude_entities";
import {IAmplitudeFavouritesEventParams} from "../tracking/get_amplitude_favourites_event_properties";
import {trackAmplitudeAddToComparison} from "../tracking/track_amplitude_add_to_comparison";
import {trackAmplitudeAddToFavourites} from "../tracking/track_amplitude_add_to_favourites";

interface IProps {
    offerId: number;
    propertyId: number;
}

export const PropertyHeaderCompareButton = (props: IProps) => {
    const {offerId, propertyId} = props;
    const {addPropertyToComparison, removePropertyFromComparison, isPropertyInComparison, isPropertyInFavourites} = useFavourites();
    const {offer, property} = useFavouritesAmplitudeEntities({offerId, propertyId});
    const isAuthenticated = useIsAuthenticated();
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const comparatorLink = useKMLink(kmAppLink.site.offers.favouritesComparatorWithParams);
    const isInComparison = isPropertyInComparison(propertyId);
    const comparisonEventParams: IAmplitudeFavouritesEventParams = {
        location: "property_page",
        favouritePlacement: "rp_favourite_property_compare",
        offer,
        property
    };

    const handleComparisonClick = async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);

        try {
            if (isInComparison) {
                setIsTooltipOpen(false);
                await removePropertyFromComparison(propertyId);
                return;
            }

            const wasAlreadyInFavourites = isPropertyInFavourites(propertyId);
            const wasAdded = await addPropertyToComparison(propertyId);

            if (wasAdded && isAuthenticated) {
                trackAmplitudeAddToComparison(comparisonEventParams);

                if (!wasAlreadyInFavourites) {
                    trackAmplitudeAddToFavourites(comparisonEventParams);
                }

                setIsTooltipOpen(true);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Hidden className={compareButtonWrapperStyle} on="mobile">
            <ManualPopover
                className={compareTooltipStyle}
                handleSetIsOpen={setIsTooltipOpen}
                hideArrow
                isOpen={isTooltipOpen}
                placement="bottom-end"
                popover={
                    <div
                        aria-live="polite"
                        className={compareTooltipBodyStyle}
                        data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_TOOLTIP}
                        onClick={(event) => event.stopPropagation()}
                        role="status"
                    >
                        <Text as="p" color="var(--colors-white)" variant="info_txt_1">
                            Oferta została dodana do porównywarki i zapisana w ulubionych.{" "}
                            <a
                                className={compareTooltipLinkStyle}
                                data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_TOOLTIP_LINK}
                                href={comparatorLink}
                                onClick={() => {
                                    trackAmplitudeButtonClick({
                                        buttonName: "rp_go_to_comparison",
                                        value: "km_offer_comparison",
                                        buttonCategory: "property_page",
                                        itemId: offerId,
                                        propertyId,
                                        buttonLink: comparatorLink
                                    });
                                }}
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                Przejdź do porównywarki
                            </a>
                        </Text>
                        <Button
                            aria-label="Zamknij"
                            className={compareTooltipCloseStyle}
                            iconLeft={CloseIcon}
                            iconLeftProps={{
                                fill: "var(--colors-white)",
                                size: "1.6",
                                wrapperColor: "transparent",
                                wrapperSize: "1.6"
                            }}
                            onClick={() => setIsTooltipOpen(false)}
                            type="button"
                            variant="transparent"
                        />
                    </div>
                }
            >
                {({refs, getReferenceProps}) => (
                    <div ref={refs.setReference} {...getReferenceProps()} onClick={(event) => event.stopPropagation()}>
                        <Button
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.COMPARE_HEADER_BUTTON}
                            iconLeft={BrandWeightIcon}
                            iconLeftProps={{className: compareIconStyle}}
                            isLoading={isLoading}
                            onClick={handleComparisonClick}
                            size="small"
                            type="button"
                            variant={isInComparison ? "filled_secondary" : "outlined_secondary"}
                        >
                            {isInComparison ? "Usuń z porównywarki" : "Dodaj do porównania"}
                        </Button>
                    </div>
                )}
            </ManualPopover>
        </Hidden>
    );
};

const compareButtonWrapperStyle = css({
    ml: "auto"
});

const compareIconStyle = css({
    mr: 1.5
});

const compareTooltipStyle = css({
    zIndex: "2",
    mt: "1rem"
});

const compareTooltipBodyStyle = css({
    display: "flex",
    position: "relative",
    alignItems: "flex-start",
    gap: 1,
    p: 1,
    w: "22.4rem",
    bg: "var(--colors-gray-800)",
    borderRadius: 1,
    boxShadow: "var(--shadow-deep)",
    _before: {
        content: '""',
        position: "absolute",
        top: "-1.2rem",
        right: 3,
        w: "3.1rem",
        h: "1.2rem",
        bg: "var(--colors-gray-800)",
        clipPath: "polygon(50% 0%, 100% 100%, 0% 100%)"
    }
});

const compareTooltipLinkStyle = css({
    color: "inherit",
    textDecoration: "underline"
});

const compareTooltipCloseStyle = css({
    flex: "0 0 auto",
    minWidth: "unset",
    width: "auto",
    h: "unset",
    p: 0
});
